import {
  DatePipe,
  FormatWidth,
  FormStyle,
  getLocaleDateFormat,
  getLocaleDayNames,
  getLocaleMonthNames,
  getLocaleNumberFormat,
  getLocaleTimeFormat,
  NumberFormatStyle,
  registerLocaleData,
  TranslationWidth,
} from "@angular/common";
import { HttpClient } from "@angular/common/http";
import localede from "@angular/common/locales/de";
import localees from "@angular/common/locales/es";
import localefr from "@angular/common/locales/fr";
import localeit from "@angular/common/locales/it";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ClientSettings } from "src/model/class/client-settings";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { NumberFormat } from "src/model/class/general classes/number-format";
import { Translation } from "src/model/class/general classes/translation";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { clientSettingsUpdateLanguageAction } from "src/state/actions/client-settings.actions";
import {
  translationForceReloadAction,
  translationLoadInitStartAction,
} from "src/state/actions/translation.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { listTranslation } from "src/state/selectors/translation.selector";
import { AuthenticationService } from "./authentication.service";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class TranslationService implements iMqttHandling {
  private _logContext: string = "translation.service";

  private _selectCurrentUserSub: Subscription = null;
  private _selectClientSettingsUserSub: Subscription = null;
  private _listTranslationSubscription: Subscription = null;

  private _currentClientSetting: ClientSettings = null;

  private _currentUser: CurrentSessionData = null;

  private _localID: string = "";
  private _localFormatNumber: string = "";
  private _localFormatDate: string = "";
  private _localFormatTime: string = "";

  private _languageInUse: string = "it";

  private _translations = new Map<string, Translation>();

  _eventSubscriptions: Array<Subscription> = null;
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  /**
   * Return current language
   */
  public get currentLanguage() {
    return this._languageInUse;
  }

  /**
   * Return defualt decimal separator
   */
  get defaultDecimalSeparator() {
    return this._localFormatNumber.substring(5, 1);
  }

  /**
   * Return thousand separator
   */
  get defaultThousandSeparator() {
    return this._localFormatNumber.substring(1, 1);
  }

  /**
   * Return defualt date separator
   */
  get defaultDateSeparator() {
    return this._localFormatDate.substring(2, 3);
  }

  /**
   * Return defualt time separator
   */
  get defaultTimeSeparator() {
    return this._localFormatDate.substring(8, 1);
  }

  /**
   * Day abbreviate name
   */
  get dayNameAbbreviated() {
    return getLocaleDayNames(
      this._languageInUse,
      FormStyle.Format,
      TranslationWidth.Abbreviated
    );
  }

  /**
   * Day name long
   */
  get dayNameLong() {
    return getLocaleDayNames(
      this._languageInUse,
      FormStyle.Format,
      TranslationWidth.Wide
    );
  }

  /**
   * Month name abbreviate
   */
  get monthNameAbbreviate() {
    return getLocaleMonthNames(
      this._languageInUse,
      FormStyle.Format,
      TranslationWidth.Abbreviated
    );
  }

  /**
   * Month name long
   */
  get monthNameLong() {
    return getLocaleMonthNames(
      this._languageInUse,
      FormStyle.Format,
      TranslationWidth.Wide
    );
  }

  constructor(
    private _translateService: TranslateService,
    private _storageService: LocalStorageService,
    private _datePipe: DatePipe,
    private _http: HttpClient,
    private _eventService: EventService,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _mqttServiceNative: MqttService,
    private _authService: AuthenticationService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();
    this._eventSubscriptions = new Array<Subscription>();

    this.subscribeSettingServiceData();

    registerLocaleData(localeit);
    registerLocaleData(localede);
    registerLocaleData(localefr);
    registerLocaleData(localees);

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
    this.loadCurrentUser();
    this.loadListTranslation();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();

    this.unsubscibeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  /*
   * GF: when the app is started check if a default language for that client is already defined: if yes use it, otherwise
   * initialize the default language as the system default one.
   */
  public async loadDefaultLanguage() {
    if (this._currentClientSetting) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "loadDefaultLanguage",
        "language in Settings: " + this._currentClientSetting?.language
      );

      this._languageInUse = this._currentClientSetting.language;

      if (!this._currentClientSetting?.language) {

        this._loggerService.log(
          LogLevel.LOG_WARNING,
          this._logContext,
          this._logContext,
          "loadDefaultLanguage",
          "_currentClientSetting.language is undefined! language in use: 'en' - clientSettings: " +
            JSON.stringify(this._currentClientSetting)
        );
        this._languageInUse = "en";
      }

      await this.changLang(this._languageInUse);
    } else {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadDefaultLanguage",
        "_currentClientSetting is undefined!"
      );
    }


  }

  /**
   * GF: when a user click on the language flag and change the language the language is changed,
   * the localformat is reloaded and the storage is updated
   *
   * @param language new language to use
   */
  public changLang(language: string): void {
    language = language.trim();

    this.setLocaleByLanguage(language);
    this.reloadLocalFormat();

    this._translateService.use(language);

    if (this._currentClientSetting) {
      this._languageInUse = language;
      this._eventService.OnLanguageChanged.next(null);

      this._store.dispatch(
        clientSettingsUpdateLanguageAction({ lang: language })
      );
    }
  }

  /**
   * Format specific timestamp with specific format
   *
   * @param timestamp
   * @returns
   */
  public formatDateTime(timestamp: moment.Moment): string {
    return (
      timestamp.format(this._localFormatDate.toUpperCase()) +
      " " +
      timestamp.format(this._localFormatTime)
    );
  }

  /**
   * Format number to decimal
   *
   * @param value Value to format
   * @param showThousandSeparator Show thousand separator
   * @param decimalDigits Decimal digits to show
   * @returns
   */
  public formatDecimal(
    value: number,
    showThousandSeparator: boolean,
    decimalDigits: number
  ): string {
    const format: NumberFormat = new NumberFormat();
    format.maximumFractionDigits = decimalDigits;
    format.minimumFractionDigits = decimalDigits;

    if (showThousandSeparator) {
      return value.toLocaleString(this._languageInUse, format);
    } else {
      return value
        .toLocaleString(this._languageInUse, format)
        .replace(this.defaultThousandSeparator, "");
    }
  }

  /**
   * Format number to decimal for db, using . instead of ,
   *
   * @param value Value to format
   * @param showThousandSeparator Show thousand separator
   * @param decimalDigits Decimal digits to show
   * @returns
   */
  public formatDecimalForDB(value: number) {
    return value.toString().replace(",", ".");
  }

  public countDecimal(value: string): number {
    const valueSplit: string[] = value.split(".");
    if (valueSplit.length === 1) {
      return 0;
    } else {
      return valueSplit[1].length;
    }
  }

  /**
   * Reload current date/time format by locale kind
   */
  private reloadLocalFormat(): void {
    this._localFormatNumber = getLocaleNumberFormat(
      this._localID,
      NumberFormatStyle.Decimal
    );
    this._localFormatDate = getLocaleDateFormat(
      this._localID,
      FormatWidth.Short
    );
    this._localFormatTime = getLocaleTimeFormat(
      this._localID,
      FormatWidth.Medium
    );

    if (this._datePipe.hasOwnProperty("locale")) {
      this._datePipe["locale"] = this._localID;
    } else if (this._datePipe.hasOwnProperty("_locale")) {
      this._datePipe["_locale"] = this._localID;
    }
  }

  /**
   * Set current locale by language value
   *
   * @param language Language to set locale
   */
  private setLocaleByLanguage(language: string): void {
    switch (language) {
      case "it":
        this._localID = "it-IT";
        break;
      case "de":
        this._localID = "de-DE";
        break;
      case "es":
        this._localID = "es-ES";
        break;
      case "en":
        this._localID = "en-EN";
        break;
      case "fr":
        this._localID = "fr-FR";
        break;
      default:
        this._localID = "en-EN";
        break;
    }
  }

  /**
   * Subsribe to public Observable "clientSettings" of "settings.service", which propagates the info about the general app
   * -related settings as defined for this device; it will also emit each time these settings are changed.
   */
  private subscribeSettingServiceData(): void {
    this._selectClientSettingsUserSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSetting) => clientSetting !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._currentClientSetting = clientSettings;
      });
  }

  private subscribeEvents() {
    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "LOCAL MAP-SUBSCRIPTIONS 1: "
        );
        for (const entry of Array.from(
          this._mqttAddressSubscriptionsMap.entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
          );
        }

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 1."
        );
        for (const entry of Array.from(
          new Map(Object.entries(this._mqttServiceNative.observables)).entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 1 Entry:  " + entry[0]
          );
        }

        await this.unsubscribeToMQTTAddresses();

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "LOCAL MAP-SUBSCRIPTIONS 2: "
        );
        for (const entry of Array.from(
          this._mqttAddressSubscriptionsMap.entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
          );
        }

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY 2."
        );
        for (const entry of Array.from(
          new Map(Object.entries(this._mqttServiceNative.observables)).entries()
        )) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_eventService",
            "OnMakeLogout",
            "NARTIVE ARRAY 2 Entry:  " + entry[0]
          );
        }
      })
    );
  }

  unsubscibeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#region Mqtt addresses handling

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_RELOAD_TRANSLATION
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_RELOAD_TRANSLATION;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable.subscribe((message) => {
      this._store.dispatch(translationForceReloadAction());
    });

    let res = await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
    if (res) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses",
        mqttAddress + " SETTED"
      );
    }

    //#endregion

    //#region Subscribing to ADDRESS_LANGUAGE
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.ADDRESS_LANGUAGE;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable.subscribe(
      (message: IMqttMessage) => {
        const payloadMessage = JSON.parse(message.payload.toString()).Message;

        if (payloadMessage.ClientID == this._mqttService.clientID) {
          this.changLang(payloadMessage.Value.toString().toLowerCase());
        }
      }
    );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  /**
   * Check if device language is supported by the system
   *
   * @param languageToTest Device language
   * @returns
   */
  public checkIfDeviceLanguageIsSupportedBySystem(
    languageToTest: string
  ): boolean {

    return (
      AppSettings.LANGUAGE_SYSTEM_AVAIABLE.indexOf(
        languageToTest.toLowerCase()
      ) >= 0
    );
  }

  /**
   * Call API to load translations
   */
  public getTranslation(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_TRANSLATION
    );
  }

  /**
   * Call API to load translations if changed from last read
   *
   * @param translationStore Current translaiton store
   * @returns
   */
  public getTranslationByLastModify(
    translationStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = (translationStore?.lastAPIModified || "");

    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_TRANSLATION +
        queryString
      )
      .pipe(
        map((returnedTranslation) => {
          if (returnedTranslation) {
            translationStore = returnedTranslation;
          }

          return translationStore;
        })
      );
  }

  /**
   * Popuplate internal service map by list translation
   *
   * @param listTranslation List translation input from object
   */
  private populateServiceMap(listTranslation: any): void {
    listTranslation.map((transation) => {
      this._translations.set(transation.identify, transation);
    });
  }

  /**
   * Take a string and try to translate it
   * @param stringToTranslate string to translate
   * @returns
   */
  public TranslateText(stringToTranslate: string): string {
    let posStart = stringToTranslate.indexOf("{{");
    let posEnd = 0;
    let substringToTranslate = "";

    while (posStart >= 0) {
      posEnd = stringToTranslate.indexOf("}}");
      substringToTranslate = stringToTranslate.substring(posStart + 2, posEnd);
      stringToTranslate = stringToTranslate.replace(
        "{{" + substringToTranslate + "}}",
        this.TranslateSingleWord(substringToTranslate)
      );
      posStart = stringToTranslate.indexOf("{{", posStart + 1);
    }

    return stringToTranslate;
  }

  /**
   * This method calculates the name of the correct language directory to be used by the "Tinymce" plugin to localise the elements (options, menu entries)
   * of any "Editor" html component used in templates. It does so considering the currently selected language, as defined inside this service.
   *
   * NOTE: Any Tinymce loc directory should be downloaded from "https://www.tiny.cloud/get-tiny/language-packages/" and be pasted in "\node_modules\tinymce\langs"
   * @returns The name of the localisation directory to be used by "Tinymce" plugin
   */
  public getTinymceLocalisationDir(): string {
    let locDir: string = "";

    switch (this._translateService.getCurrentLang()) {
      case "it":
        locDir = "it";
        break;
      case "de":
        locDir = "de";
        break;
      case "es":
        locDir = "es";
        break;
      case "fr":
        locDir = "fr_FR";
        break;

      case "en":
      default:
        locDir = "en_GB";
        break;
    }

    return locDir;
  }

  /**
   * Get a string a return the translation on the basis of the current language
   * @param stringToTranslate
   * @returns the string translated or the original string if the language is not setted or the traslation is not found
   */
  private TranslateSingleWord(stringToTranslate: string): string {

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "TranslateSingleWord()",
      "TEXT TO TRANSLATE: " + stringToTranslate
    );

    if (this._translations.has(stringToTranslate)) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "TranslateSingleWord()",
        "TRANSLATE"
      );

      switch (this._localID) {
        case "it-IT":
          return this._translations.get(stringToTranslate).itString;
          break;
        case "de-DE":
          this._localID;
          return this._translations.get(stringToTranslate).deString;
          break;
        case "es-ES":
          return this._translations.get(stringToTranslate).esString;
          break;
        case "en-EN":
          return this._translations.get(stringToTranslate).enString;
          break;
        case "fr-FR":
          return this._translations.get(stringToTranslate).frString;
          break;
        default:
          break;
      }
    }
    return stringToTranslate;
  }
  //#endregion

  /**
   * Load current user from store
   */
  private loadCurrentUser(): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_store.select(currentUser)",
      "loadCurrentUser()",
      "START"
    );

    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        //Reload translation only when user change and pass from null to evaluated object
        distinctUntilChanged(
          (
            previousSession: GenericUserData,
            currentSession: GenericUserData
          ) => {
            let accessTokenPreviosSession: string =
              previousSession?.currentUser?.accessToken || "";
            let accessTokenCurrentSession: string =
              currentSession?.currentUser?.accessToken || "";

            return accessTokenPreviosSession === accessTokenCurrentSession;
          }
        ),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser),
        tap((currentUser: CurrentSessionData) => {
          if (
            currentUser &&
            this._authService.isAccessTokenValid(currentUser) &&
            this._authService.isRefreshTokenValid(currentUser)
          ) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(currentUser)",
              "loadCurrentUser()",
              "REACT"
            );
            this._store.dispatch(translationLoadInitStartAction());
          }
        })
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this._currentUser = currentUser;
      });
  }

  /**
   * Load translation from store selection
   */
  private loadListTranslation(): void {
    this._listTranslationSubscription = this._store
      .select(listTranslation)
      .pipe(
        filter(
          (listTranslationObject: GenericStoreApiData) => !!listTranslationObject
        ),
        filter(
          (listTranslationObject: GenericStoreApiData) =>
            listTranslationObject?.loaded
        ),
        tap(() => this._translations.clear()),
        /*
        filter(
          (listTranslationObject: GenericStoreApiData) =>
            this._currentUser !== null
        ),
        */
        filter(
          (listTranslationObject: GenericStoreApiData) =>
            !(
              listTranslationObject.errorGeneric ||
              listTranslationObject.errorNotYetImplemented
            )
        ),
        tap((listTranslationObject: GenericStoreApiData) => {
          if (listTranslationObject.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listTranslation)",
              "loadListTranslation()",
              "We are using old data!!"
            );
          }
        }),
        map(
          (listTranslationObject: GenericStoreApiData) =>
            listTranslationObject.data?.data || []
        )
      )
      .subscribe((listTranslation: any) => {
        this.populateServiceMap(listTranslation);
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectClientSettingsUserSub?.unsubscribe();
    this._selectCurrentUserSub?.unsubscribe();
    this._listTranslationSubscription?.unsubscribe();
  }
  //#endregion
}
